import { useState, useMemo, useRef } from "react";
import { useTranslate } from "@refinedev/core";

import { usePaginatedTable, useQueryParams } from "@shared/hooks";
import { ModalRef } from "@shared/components";
import { AudioFile } from "@modules/audioFiles/types";
import { useFetchResourcesByIdsQuery } from "@shared/services/queries";
import { ApiResponse } from "@shared/types";
import { Book } from "@modules/books/types";

export function useAudioFileList() {
  const [selectedRows, setSelectedRows] = useState<number[]>([]);

  const createAudioFileModalRef = useRef<ModalRef>(null);
  const editAudioFileModalRef = useRef<ModalRef>(null);

  const translate = useTranslate();
  const { getQueryParams } = useQueryParams();

  const { rows, loading, refetch, ...tableProps } = usePaginatedTable<AudioFile>({
    resource: "v1/audiobook_admin",
    filters: getQueryParams(),
    sorters: { ordering: "-id" },
  });

  const bookIds = useMemo(
    () => [...new Set(rows?.map((audioBook) => audioBook.book).filter((id): id is number => !!id))],
    [rows]
  );

  const { data: books } = useFetchResourcesByIdsQuery<ApiResponse<Book>>({
    resource: "v1/book_admin",
    ids: bookIds,
  });

  const dataToExport = useMemo(() => {
    return selectedRows.length > 0
      ? rows.filter((row) => selectedRows.includes(row.id))
      : rows;
  }, [selectedRows, rows]);

  return {
    rows,
    loading,
    tableProps,
    books: books?.results,
    selectedRows,
    dataToExport,
    createAudioFileModalRef,
    editAudioFileModalRef,
    refetch,
    setSelectedRows,
    translate,
  };
}
